import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ProfileService from 'services/profile-services'

export interface Neighborhood {
  id: number
  name: string
}

interface UsePostalCodeLookup {
  postalCode: string
  setPostalCode: (value: string) => void
  neighborhoods: Neighborhood[]
  selectedNeighborhood: Neighborhood | null
  setSelectedNeighborhood: (neighborhood: Neighborhood | null) => void
  municipality: string
  state: string
  hasError: boolean
}

const ERROR_MESSAGE = 'Ups! No hemos encontrado el código postal, chequea nuevamente.'

// Reusable extraction of the StepCP mechanism (survey postalCode module):
// debounced ProfileService.checkZip lookup that autofills state/municipality
// and offers the neighborhoods list. StepCP itself is untouched (it can be
// migrated to this hook later).
const usePostalCodeLookup = (): UsePostalCodeLookup => {
  const [postalCode, setPostalCode] = useState('')
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([])
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Neighborhood | null>(null)
  const [municipality, setMunicipality] = useState('')
  const [stateName, setStateName] = useState('')
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const handler = setTimeout(async () => {
      const clearDependents = () => {
        setNeighborhoods([])
        setSelectedNeighborhood(null)
        setMunicipality('')
        setStateName('')
      }

      if (!postalCode || postalCode.length <= 4) {
        clearDependents()
        setHasError(false)
        return
      }

      try {
        // Session read from localStorage, same approach as StepCP (useSession
        // re-renders would loop inside the debounce).
        const ls = localStorage.getItem('state')
        const dataUser = ls ? JSON.parse(ls) : {}

        toast.loading('Buscando código postal', { position: 'top-center' })
        const response = await ProfileService.checkZip({ postalCode }, { session: dataUser?.data })
        toast.dismiss()

        if (response.status === 204) {
          toast.error(ERROR_MESSAGE, { position: 'bottom-center' })
          clearDependents()
          setHasError(true)
          return
        }

        setHasError(false)
        // A new postal code invalidates the previously selected neighborhood
        setSelectedNeighborhood(null)
        setStateName(response.data[0].name)
        setMunicipality(response.data[0].cities[0].name)
        setNeighborhoods(response.data[0].cities[0].neighborhoods ?? [])
      } catch {
        toast.dismiss()
        toast.error(ERROR_MESSAGE, { position: 'bottom-center' })
        clearDependents()
        setHasError(true)
      }
    }, 1000)

    return () => clearTimeout(handler)
  }, [postalCode])

  return {
    postalCode,
    setPostalCode,
    neighborhoods,
    selectedNeighborhood,
    setSelectedNeighborhood,
    municipality,
    state: stateName,
    hasError
  }
}

export default usePostalCodeLookup
